import { FeedbackOrigin } from '../../../../components/ai/DisclosureControls';
import { getFeedbackPreference } from '../../../../components/ai/aiDisclosure';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './textAnswer.css';

export const TextAnswer = ({
  placeholder,
  answer,
  onChange,
  onDraftChange,
  tabIndex,
  requireConfirmation = true,
}) => {
  const { t } = useTranslation();
  const [aiOrigin, setAiOrigin] = useState(getFeedbackPreference());
  const [draftText, setDraftText] = useState(answer.answer || '');
  const originRef = useRef(aiOrigin);
  originRef.current = aiOrigin;
  // use reference instead of state in order for setTimeout scope to have the most recent value
  // (otherwise, it gets locked to when the timeout started)
  const answerText = useRef(answer.answer || '');
  const savedAnswer = useRef(answer.answer || '');
  const submitTimer = useRef(null);
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    savedAnswer.current = answer.answer || '';
  }, [answer.answer]);
  onChangeRef.current = onChange;

  useEffect(
    () => () => {
      if (submitTimer.current) {
        clearTimeout(submitTimer.current);
      }
      if (
        (!requireConfirmation || originRef.current === 'personal') &&
        answerText.current !== savedAnswer.current
      ) {
        onChangeRef.current(answerText.current, originRef.current);
      }
    },
    []
  );

  const handleTextChange = event => {
    const value = event.target.value;
    answerText.current = value;
    setDraftText(value);
    onDraftChange?.(value, originRef.current);
    if (submitTimer.current) {
      clearTimeout(submitTimer.current);
    }
    submitTimer.current = setTimeout(function () {
      submit();
      submitTimer.current = null;
    }, 500); // submit after 0.5 sec of inactivity
  };

  const submitNow = () => {
    if (submitTimer.current) {
      clearTimeout(submitTimer.current);
      submitTimer.current = null;
    }
    submit();
  };

  const handleKeyDown = event => {
    if (event.key === 'Tab') {
      submitNow();
    } else if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      submitNow();
    }
  };

  const submit = () => {
    // update if the answer has changed
    if (
      (!requireConfirmation || originRef.current === 'personal') &&
      answerText.current !== savedAnswer.current
    ) {
      const nextAnswer = answerText.current;
      savedAnswer.current = nextAnswer;
      onChangeRef.current(nextAnswer, originRef.current);
    }
  };

  return (
    <>
      {requireConfirmation && (
        <FeedbackOrigin
          value={aiOrigin}
          onChange={value => {
            setAiOrigin(value);
            originRef.current = value;
            onDraftChange?.(answerText.current, value);
            if (
              value === 'personal' &&
              answerText.current !== savedAnswer.current
            )
              submit();
          }}
        />
      )}
      {requireConfirmation && aiOrigin !== 'personal' && (
        <small>Confirm above to save your answer.</small>
      )}
      <textarea
        tabIndex={tabIndex}
        className="br-survey-text-answer"
        aria-label={placeholder || t('Answer')}
        rows={2}
        placeholder={placeholder || t('Answer')}
        value={draftText}
        onChange={handleTextChange}
        onBlur={submitNow}
        onKeyDown={handleKeyDown}
      />
    </>
  );
};

export default TextAnswer;
